import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";

import { getTurnstileConfig, postContactGeneral } from "../../api/Message";

import PortalModal from "../PortalModal";

const initialData = {
    lastname: "",
    firstname: "",
    email: "",
    content: "",
};

export default function PublicForm() {
    const [data, setData] = useState(initialData);

    const [siteKey, setSiteKey] = useState("");
    const [captchaToken, setCaptchaToken] = useState("");

    const [isLoading, setIsLoading] = useState(false);
    const [isFinish, setIsFinish] = useState(false);
    const [error, setError] = useState("");

    const turnstileRef = useRef(null);
    const submittingRef = useRef(false);

    // --------------------------------------------------
    // RÉCUPÉRATION DE LA CLÉ PUBLIQUE DEPUIS L'API
    // --------------------------------------------------

    useEffect(() => {
        let active = true;

        const loadTurnstileConfig = async () => {
            try {
                const config = await getTurnstileConfig();

                if (active) {
                    if (!config?.siteKey) {
                        throw new Error("Clé Turnstile absente");
                    }

                    setSiteKey(config.siteKey);
                }
            } catch (err) {
                console.error("Erreur configuration Turnstile :", err);

                if (active) {
                    setError("Le service de vérification est indisponible. Veuillez réessayer plus tard.");
                }
            }
        };

        loadTurnstileConfig();

        return () => {
            active = false;
        };
    }, []);

    // --------------------------------------------------
    // CHANGEMENT DES CHAMPS
    // --------------------------------------------------

    const handleChange = (e) => {
        const { name, value } = e.target;

        setData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // --------------------------------------------------
    // RÉINITIALISATION DU CAPTCHA
    // --------------------------------------------------

    const resetCaptcha = () => {
        setCaptchaToken("");
        turnstileRef.current?.reset();
    };

    // --------------------------------------------------
    // ENVOI DU FORMULAIRE
    // --------------------------------------------------

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (submittingRef.current) return;

        if (!captchaToken) {
            setError("Veuillez valider le CAPTCHA avant l'envoi.");
            return;
        }

        submittingRef.current = true;
        setIsLoading(true);
        setError("");

        try {
            await postContactGeneral(data.lastname, data.firstname, data.email, data.content, captchaToken);

            setData(initialData);
            setIsFinish(true);
        } catch (err) {
            console.error("Erreur lors de l'envoi :", err);

            setError("Une erreur est survenue lors de l'envoi du message. Veuillez réessayer.");
        } finally {
            resetCaptcha();
            submittingRef.current = false;
            setIsLoading(false);
        }
    };

    // --------------------------------------------------
    // RENDER
    // --------------------------------------------------

    return (
        <>
            <form className="flex flex-col" autoComplete="on" onSubmit={handleSubmit}>
                {/* NOM ET PRÉNOM */}

                <div className="name-container flex gap-[24px] w-full">
                    <div className="flex flex-col gap-[8px] w-full">
                        <label htmlFor="lastname">
                            Nom <span className="text-primary">*</span>
                        </label>

                        <input required type="text" name="lastname" id="lastname" autoComplete="family-name" onChange={handleChange} value={data.lastname} />
                    </div>

                    <div className="flex flex-col gap-[8px] w-full">
                        <label htmlFor="firstname">
                            Prénom <span className="text-primary">*</span>
                        </label>

                        <input required type="text" name="firstname" id="firstname" autoComplete="given-name" onChange={handleChange} value={data.firstname} />
                    </div>
                </div>

                {/* EMAIL */}

                <div className="flex flex-col gap-[8px]">
                    <label htmlFor="email">
                        Adresse email <span className="text-primary">*</span>
                    </label>

                    <input required type="email" name="email" id="email" autoComplete="email" onChange={handleChange} value={data.email} />
                </div>

                {/* MESSAGE */}

                <div className="flex flex-col gap-[8px]">
                    <label htmlFor="content">
                        Message <span className="text-primary">*</span>
                    </label>

                    <textarea required name="content" id="content" rows="5" onChange={handleChange} value={data.content} />
                </div>

                {/* CLOUDFLARE TURNSTILE */}

                <div className="my-6">
                    {siteKey ? (
                        <Turnstile
                            ref={turnstileRef}
                            siteKey={siteKey}
                            options={{
                                theme: "auto",
                                size: "normal",
                            }}
                            onSuccess={(token) => {
                                setCaptchaToken(token);
                                setError("");
                            }}
                            onExpire={() => {
                                setCaptchaToken("");
                            }}
                            onError={() => {
                                setCaptchaToken("");
                                setError("La vérification CAPTCHA a échoué. Veuillez réessayer.");
                            }}
                        />
                    ) : (
                        <p className="text-sm text-white">Chargement de la vérification de sécurité...</p>
                    )}
                </div>

                {/* ERREUR */}

                {error && (
                    <p role="alert" className="text-red-400 text-sm mb-4">
                        {error}
                    </p>
                )}

                {/* BOUTON ENVOYER */}

                <button type="submit" disabled={isLoading || !captchaToken} className="w-full bg-primary text-center disabled:opacity-50 disabled:cursor-not-allowed">
                    {isLoading ? "Envoi en cours..." : "Envoyer"}
                </button>
            </form>

            {/* LOADER */}

            {isLoading && (
                <div className="justify-center items-center flex flex-col gap-4 p-4">
                    <div className="loader"></div>
                </div>
            )}

            {/* MODALE DE CONFIRMATION */}

            <PortalModal isOpen={isFinish} onClose={() => setIsFinish(false)}>
                <div className="text-center flex flex-col items-center justify-center gap-4">
                    <p className="font-bold text-4xl">Message envoyé avec succès !</p>

                    <p className="text-lg">Nous vous contacterons dès que possible.</p>

                    <Link to="/" className="w-fit py-2 px-3 font-bold bg-primary text-center rounded-lg">
                        Retour à l'accueil
                    </Link>
                </div>
            </PortalModal>
        </>
    );
}
